import { __ } from "@wordpress/i18n";
import { useState, useRef, useEffect } from "@wordpress/element";
import { SketchPicker } from "react-color";
import { X } from "lucide-react";

function hexToRgb(hex) {
    if (!hex) return null;
    const normalized = hex.replace("#", "");
    if (normalized.length === 3) {
        const r = parseInt(normalized[0] + normalized[0], 16);
        const g = parseInt(normalized[1] + normalized[1], 16);
        const b = parseInt(normalized[2] + normalized[2], 16);
        return { r, g, b };
    }
    if (normalized.length === 6) {
        const r = parseInt(normalized.substring(0, 2), 16);
        const g = parseInt(normalized.substring(2, 4), 16);
        const b = parseInt(normalized.substring(4, 6), 16);
        return { r, g, b };
    }
    return null;
}

function rgbToHex({ r, g, b }) {
    const toHex = (n) => {
        const h = n.toString(16);
        return h.length === 1 ? "0" + h : h;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function parseColorString(val) {
    if (!val) return null;
    val = String(val).trim();
    // rgba or rgb
    if (val.toLowerCase().startsWith("rgba")) {
        const m = val.match(
            /rgba?\(\s*(\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\s*\)/,
        );
        if (m) {
            return {
                hex: rgbToHex({ r: +m[1], g: +m[2], b: +m[3] }),
                rgb: { r: +m[1], g: +m[2], b: +m[3], a: +(m[4] ?? 1) },
            };
        }
    } else if (val.toLowerCase().startsWith("rgb")) {
        const m = val.match(/rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/i);
        if (m) {
            return {
                hex: rgbToHex({ r: +m[1], g: +m[2], b: +m[3] }),
                rgb: { r: +m[1], g: +m[2], b: +m[3], a: 1 },
            };
        }
    } else {
        // allow hex with or without #
        const maybeHex = val.startsWith("#") ? val : `#${val}`;
        const rgb = hexToRgb(maybeHex);
        if (rgb) {
            return { hex: maybeHex, rgb: { ...rgb, a: 1 } };
        }
    }
    return null;
}

export default function ColorPicker({ name, value, defaultColor, onChange }) {
    // internal state always as { hex, rgb: {r,g,b,a} }
    const [color, setColor] = useState(() => {
        const parsed = parseColorString(value);
        return parsed || null;
    });
    //console.log(color);
    // console.log(defaultColor);
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);

    // keep internal color in sync when prop changes
    useEffect(() => {
        const parsed = parseColorString(value);
        if (parsed) {
            setColor(parsed);
        }
    }, [value]);

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                pickerRef.current &&
                !pickerRef.current.contains(event.target)
            ) {
                setShowPicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const emitChange = (c) => {
        // Handle clearing: accept null or empty string to mean clear value
        if (c === null || c === "") {
            if (typeof onChange === "function") onChange(name, "");
            return;
        }

        // c: { hex, rgb: {r,g,b,a} }
        if (!c || typeof c !== "object") return;
        const { hex, rgb } = c;
        const a = rgb && typeof rgb.a !== "undefined" ? rgb.a : 1;
        const out =
            a !== 1
                ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${+a.toFixed(2)})`
                : hex;
        if (typeof onChange === "function") onChange(name, out);
    };

    const handlePickerChange = (newColor) => {
        // newColor from react-color has .hex and .rgb (with .a)
        const next = {
            hex: newColor.hex,
            rgb: {
                r: newColor.rgb.r,
                g: newColor.rgb.g,
                b: newColor.rgb.b,
                a: typeof newColor.rgb.a === "number" ? newColor.rgb.a : 1,
            },
        };
        setColor(next);
        emitChange(next);
    };

    const showReset = (() => {
        if (!defaultColor || !color) return false;
        const parsedDefault = parseColorString(defaultColor);
        if (!parsedDefault) return false;
        // Compare hex lowercase, and also alpha if present
        const sameHex =
            parsedDefault.hex.toLowerCase() === color.hex?.toLowerCase();
        const sameAlpha =
            Math.abs((parsedDefault.rgb?.a ?? 1) - (color.rgb?.a ?? 1)) < 0.01;
        return sameHex && sameAlpha;
    })();

    const handleReset = () => {
        const parsedDefault = parseColorString(defaultColor);
        if (parsedDefault) {
            setColor(parsedDefault);
            emitChange(parsedDefault);
            return;
        }

        // No default - clear the value
        setColor(null);
        if (typeof onChange === "function") onChange(name, "");
    };

    // input accepts hex (#fff or fff) and rgb/rgba(...) strings
    const handleInputChange = (e) => {
        const raw = String(e.target.value || "").trim();
        // try to parse rgb/rgba or hex
        const parsed = parseColorString(raw);
        if (parsed) {
            setColor(parsed);
            emitChange(parsed);
            return;
        }
        // if not parsed but looks like hex without #, accept visually
        const withoutHash = raw.replace(/^#/, "");
        if (
            /^[0-9A-Fa-f]{3}$/.test(withoutHash) ||
            /^[0-9A-Fa-f]{6}$/.test(withoutHash)
        ) {
            const normalized = `#${withoutHash}`;
            const rgb = hexToRgb(normalized);
            if (rgb) {
                const next = {
                    hex: normalized,
                    rgb: { ...rgb, a: color.rgb?.a ?? 1 },
                };
                setColor(next);
                emitChange(next);
                return;
            }
        }
        // otherwise update only the visible hex/text to allow user typing (no emit)
        setColor((prev) => ({ ...(prev || {}), hex: raw }));
    };

    const displayValue = (() => {
        if (!color) return "";
        if (color.rgb && typeof color.rgb.a === "number" && color.rgb.a !== 1) {
            // show a with max 2 decimals, remove trailing zeros
            const a = +color.rgb.a.toFixed(2);
            return `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${a})`;
        }
        return color.hex || "";
    })();

    const inputRef = useRef(null);

    return (
        <div className="relative">
            <div className="flex items-center relative">
                {/* Color Preview Button */}
                <button
                    type="button"
                    className="absolute left-[5px] top-[5px] size-7 shadow-md rounded-[0.25rem] transition-all"
                    style={{
                        backgroundColor: color
                            ? color.rgb
                                ? `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`
                                : color.hex
                            : "transparent",
                    }}
                    onClick={() => setShowPicker((prev) => !prev)}
                    onFocus={() => inputRef.current?.focus()}
                ></button>

                {/* Input: shows hex or rgba depending on alpha */}
                <input
                    ref={inputRef}
                    type="text"
                    value={displayValue}
                    onChange={handleInputChange}
                    className="px-2 py-2 pl-10 h-[38px] w-full max-w-full text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />

                {/* Reset to default color button */}
                <button
                    type="button"
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 w-4 h-4 flex items-center justify-center transition-all duration-200`}
                    onClick={handleReset}
                    aria-label={__(
                        "Reset to default color",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            {/* Popover Color Picker */}
            {showPicker && (
                <div
                    ref={pickerRef}
                    className="absolute z-50 mt-3"
                    style={{ top: "100%", left: 0 }}
                >
                    <div className="bg-white rounded-lg shadow-lg border border-gray-300">
                        <SketchPicker
                            color={color?.rgb || color?.hex}
                            onChange={handlePickerChange}
                            disableAlpha={false}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
